// api.js - Updated to match mobile app API structure
import axios from '../utils/axiosConfig';
import { getApiServerUrl } from '../utils/environment';
import logger from '../utils/logger';

// Get API base URL from environment utility
const API_BASE_URL = getApiServerUrl();

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
});

// Setup auth interceptor - handles both token injection and refresh logic
const setupAuthInterceptors = () => {
  // Request interceptor - add token to requests
  api.interceptors.request.use(
    config => {
      // Use authToken as the single source of truth (matches AuthContext)
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    error => Promise.reject(error)
  );

  // Response interceptor - handle 401 errors with token refresh
  api.interceptors.response.use(
    response => response,
    async error => {
      // Only handle 401 errors with refresh token
      if (error.response?.status === 401) {
        const refreshToken = localStorage.getItem('refreshToken');
        
        if (refreshToken) {
          try {
            const res = await api.post('/accounts/token/refresh/', { refresh: refreshToken });
            // Update token — single key only
            const newToken = res.data.access;
            localStorage.setItem('authToken', newToken);
            if (res.data.refresh) {
              localStorage.setItem('refreshToken', res.data.refresh);
            }
            
            // Retry original request with new token
            const originalRequest = error.config;
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
          } catch (refreshError) {
            clearAuthStorage();
            return Promise.reject(refreshError);
          }
        } else {
          clearAuthStorage();
        }
      }
      return Promise.reject(error);
    }
  );
};

// Helper to clear all auth storage — authToken/refreshToken only (single source of truth)
const clearAuthStorage = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userData');
  localStorage.removeItem('userType');
};

// Apply interceptors
setupAuthInterceptors();

// Wrapper for API calls with consistent error handling
const apiRequest = async (apiCall) => {
  try {
    const response = await apiCall();
    return { success: true, data: response.data, status: response.status };
  } catch (error) {
    logger.error('API error:', error);
    return {
      success: false,
      status: error.response?.status || 'unknown',
      error: error.response?.status || 'unknown',
      message: error.response?.data?.detail || error.response?.data?.message || error.message || 'Unknown error occurred'
    };
  }
};

// API endpoints mapping
export const apiEndpoints = {
  // Authentication
  login: '/accounts/login/',
  register: '/accounts/register/',
  logout: '/accounts/logout/',
  profile: '/accounts/profile/',
  user: '/accounts/user/',
  validateToken: '/accounts/validate-token/',
  changePassword: '/accounts/change-password/',
  tokenRefresh: '/accounts/token/refresh/',
  
  // Email Verification & OTP
  verifyEmail: (token) => `/accounts/verify-email/${token}/`,
  resendVerification: '/accounts/resend-verification/',
  checkEmailVerification: '/accounts/check-email-verification/',
  sendOTP: '/accounts/send-otp/',
  verifyOTP: '/accounts/verify-otp/',
  resendOTP: '/accounts/resend-otp/',
  getOTPStatus: '/accounts/otp-status/',
  
  // Orders — use orders/errands/draft/ to create, orders/my-orders/ to list
  orders: '/orders/',
  orderById: (id) => `/orders/${id}/`,
  myOrders: '/orders/my-orders/',
  errandDraft: '/orders/errands/draft/',
  errandConfirm: (id) => `/orders/errands/${id}/confirm/`,
  errandDetail: (id) => `/orders/errands/${id}/`,
  assistantMyOrders: '/orders/assistant/my-orders/',
  orderStatus: (id) => `/orders/${id}/status/`,
  orderReview: (id) => `/orders/${id}/review/`,
  orderTracking: (id) => `/orders/${id}/tracking/`,
  assignOrder: (id) => `/orders/${id}/assign/`,
  acceptOrder: (id) => `/orders/${id}/accept/`,
  cancelOrder: (id) => `/orders/${id}/cancel/`,
  
  // Users & Assistants
  assistants: '/accounts/assistants/',
  assistantById: (id) => `/accounts/assistants/${id}/`,
  verificationRequests: '/accounts/assistant-verifications/',
  verificationStatus: '/accounts/assistant/verification-status/',
  currentUser: '/accounts/user/me/',
  
  // Notifications
  notifications: '/notifications/notifications/',
  notificationMarkAsRead: (id) => `/notifications/notifications/${id}/mark_as_read/`,
  notificationMarkAllAsRead: '/notifications/notifications/mark_all_as_read/',
  notificationUnreadCount: '/notifications/notifications/unread_count/',
  pushTokens: '/notifications/push-tokens/',
  deletePushToken: '/notifications/push-tokens/delete_token/',
  
  // Dashboard Stats
  adminStats: '/admin/stats/',
  dashboardOverview: '/dashboard/overview/',
  clientStats: '/accounts/client/stats/',
  assistantStats: '/accounts/assistant/dashboard-stats/',
  handlerStats: '/accounts/handler/stats/',
  
  // Payment endpoints
  initiatePayment: '/orders/payments/initiate/',
  paymentStatus: (id) => `/orders/payments/${id}/`,
  processPayment: (id) => `/orders/payments/${id}/process/`,
  paymentCallback: '/orders/payments/callback/',
  
  // Price calculation
  calculatePrice: '/orders/calculate-price/',
  updatePriceRealtime: (id) => `/orders/${id}/update_price_realtime/`,
  
  // Referrals
  referrals: '/referrals/',
  
  // Locations
  locations: '/locations/',
  currentLocation: '/locations/current/',
  updateCurrentLocation: '/locations/current/update/',
};

// Orders API
export const ordersApi = {
  getAll:         ()           => apiRequest(() => api.get(apiEndpoints.orders)),
  getById:        (id)         => apiRequest(() => api.get(apiEndpoints.errandDetail(id))),
  getMyOrders:    ()           => apiRequest(() => api.get(apiEndpoints.myOrders)),
  getAssistantOrders: ()       => apiRequest(() => api.get(apiEndpoints.assistantMyOrders)),
  createDraft:    (data)       => apiRequest(() => api.post(apiEndpoints.errandDraft, data)),
  confirmOrder:   (id)         => apiRequest(() => api.post(apiEndpoints.errandConfirm(id))),
  assignOrder:    (id, aId)    => apiRequest(() => api.patch(apiEndpoints.assignOrder(id), { assistant_id: aId })),
  acceptOrder:    (id)         => apiRequest(() => api.post(apiEndpoints.acceptOrder(id), {})),
  cancelOrder:    (id)         => apiRequest(() => api.post(apiEndpoints.cancelOrder(id))),
  updateStatus:   (id, status) => apiRequest(() => api.patch(apiEndpoints.orderStatus(id), { status })),
  submitReview:   (id, data)   => apiRequest(() => api.post(apiEndpoints.orderReview(id), data)),
  getTracking:    (id)         => apiRequest(() => api.get(apiEndpoints.orderTracking(id))),
};

// Users API endpoints
export const usersApi = {
  getAllAssistants: () => apiRequest(() => api.get(apiEndpoints.assistants)),
  getAssistantById: (id) => apiRequest(() => api.get(apiEndpoints.assistantById(id))),
  getVerificationRequests: () => apiRequest(() => api.get(apiEndpoints.verificationRequests)),
  updateVerificationStatus: (id, status) => apiRequest(() => api.patch(`${apiEndpoints.verificationRequests}${id}/`, { status })),
  getCurrentUser: () => apiRequest(() => api.get(apiEndpoints.currentUser)),
  updateProfile: (data) => apiRequest(() => api.patch(apiEndpoints.profile, data)),
  getVerificationStatus: () => apiRequest(() => api.get(apiEndpoints.verificationStatus)),
};

// Auth API endpoints
export const authApi = {
  login: (email, password) => apiRequest(() => api.post(apiEndpoints.login, { email, password })),
  register: (userData) => apiRequest(() => api.post(apiEndpoints.register, userData)),
  logout: (refreshToken) => apiRequest(() => api.post(apiEndpoints.logout, { refresh: refreshToken })),
  validateToken: () => apiRequest(() => api.post(apiEndpoints.validateToken, {})),
  changePassword: (oldPassword, newPassword) => apiRequest(() => api.put(apiEndpoints.changePassword, {
    old_password: oldPassword,
    new_password: newPassword
  })),
  sendOTP: (email) => apiRequest(() => api.post(apiEndpoints.sendOTP, { email })),
  verifyOTP: (email, otpCode) => apiRequest(() => api.post(apiEndpoints.verifyOTP, { email, otp_code: otpCode })),
  resendOTP: (email) => apiRequest(() => api.post(apiEndpoints.resendOTP, { email })),
  checkEmailVerification: () => apiRequest(() => api.get(apiEndpoints.checkEmailVerification)),
  resendVerificationEmail: (email) => apiRequest(() => api.post(apiEndpoints.resendVerification, { email })),
};

// Payment API
export const paymentApi = {
  initiatePayment: (orderId, amount) => apiRequest(() => api.post(apiEndpoints.initiatePayment, { order_id: orderId, amount })),
  getPaymentStatus: (id) => apiRequest(() => api.get(apiEndpoints.paymentStatus(id))),
  processPayment: (id, paymentData) => apiRequest(() => api.post(apiEndpoints.processPayment(id), paymentData)),
  callback: (paymentData) => apiRequest(() => api.post(apiEndpoints.paymentCallback, paymentData)),
};

// Dashboard & Stats API
export const dashboardApi = {
  getOverview: () => apiRequest(() => api.get(apiEndpoints.dashboardOverview)),
  getAdminStats: () => apiRequest(() => api.get(apiEndpoints.adminStats)),
  getClientStats: () => apiRequest(() => api.get(apiEndpoints.clientStats)),
  getAssistantStats: () => apiRequest(() => api.get(apiEndpoints.assistantStats)),
  getHandlerStats: () => apiRequest(() => api.get(apiEndpoints.handlerStats)),
};

// Pricing API
export const pricingApi = {
  calculatePrice: (data) => apiRequest(() => api.post(apiEndpoints.calculatePrice, data)),
  updatePriceRealtime: (id, data) => apiRequest(() => api.patch(apiEndpoints.updatePriceRealtime(id), data)),
};

// Notifications API
export const notificationsApi = {
  getAll: () => apiRequest(() => api.get(apiEndpoints.notifications)),
  markAsRead: (id) => apiRequest(() => api.post(apiEndpoints.notificationMarkAsRead(id), {})),
  markAllAsRead: () => apiRequest(() => api.post(apiEndpoints.notificationMarkAllAsRead, {})),
  getUnreadCount: () => apiRequest(() => api.get(apiEndpoints.notificationUnreadCount)),
};

// Referrals API
export const referralsApi = {
  getAll: () => apiRequest(() => api.get(apiEndpoints.referrals)),
};

export default api;
